import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Clock, PoundSterling, ShieldCheck, CreditCard } from 'lucide-react';
export function RevolutionSection() {
  return <section className="bg-gold py-24 md:py-32 relative z-20 overflow-hidden" data-id="element-264">
      <div className="container mx-auto px-6 md:px-12" data-id="element-265">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} className="text-center mb-20 md:mb-28" data-id="element-266">
          <h2 className="text-primary leading-[0.9]" data-id="element-267">
            <span className="font-serif italic font-light text-4xl md:text-6xl lg:text-7xl block mb-4" data-id="element-268">
              The
            </span>
            <span className="font-heading font-bold uppercase text-5xl md:text-7xl lg:text-8xl tracking-tight block" data-id="element-269">
              Housekeeping
            </span>
            <span className="font-heading font-bold uppercase text-5xl md:text-7xl lg:text-8xl tracking-tight block" data-id="element-270">
              Revolution
            </span>
            <span className="font-script text-5xl md:text-7xl lg:text-8xl block mt-4 -rotate-2" data-id="element-271">
              is here
            </span>
          </h2>
        </motion.div>

        {/* Features + Images Grid — matching design layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12" data-id="element-272">
          {/* Left Column */}
          <div className="space-y-12" data-id="element-273">
            {/* Feature 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8
          }} className="flex flex-col gap-4" data-id="element-274">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center" data-id="element-275">
                <Clock className="w-7 h-7 text-primary" data-id="element-276" />
              </div>
              <h3 className="text-3xl md:text-4xl font-heading font-bold text-primary uppercase leading-tight tracking-wide" data-id="element-277">
                Book us anytime,
                <br data-id="element-278" />
                from anywhere
              </h3>
              <p className="text-primary/70 text-lg leading-relaxed max-w-md" data-id="element-279">
                Book a 100% online, no-hassle clean. Choose your service, date,
                and time that works best for you.
              </p>
            </motion.div>

            {/* Feature 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8,
            delay: 0.1
          }} className="flex flex-col gap-4" data-id="element-280">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center" data-id="element-281">
                <PoundSterling className="w-7 h-7 text-primary" data-id="element-282" />
              </div>
              <h3 className="text-3xl md:text-4xl font-heading font-bold text-primary uppercase leading-tight tracking-wide" data-id="element-283">
                Personalised
                <br data-id="element-284" />
                pricing online
              </h3>
              <p className="text-primary/70 text-lg leading-relaxed max-w-md" data-id="element-285">
                Get an instant quote. No hidden fees or surprises. You only pay
                for what you need.
              </p>
            </motion.div>

            {/* Image — Kitchen */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8
          }} className="h-[300px] md:h-[350px] rounded-2xl overflow-hidden shadow-2xl" data-id="element-286">
              <img src="https://cdn.magicpatterns.com/uploads/t4ZBJfen64mdo9zeXWqcrx/cleaning-commercial.jpg" alt="Professional commercial cleaning service by Smith & Jengs" loading="lazy" className="w-full h-full object-cover" data-id="element-287" />
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="space-y-12" data-id="element-288">
            {/* Image — Yellow Chair Room */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="h-[300px] md:h-[350px] rounded-2xl overflow-hidden shadow-2xl" data-id="element-289">
              <img src="https://cdn.magicpatterns.com/uploads/edhPvagv8F7uP7JU1aAsop/clean-home.jpg" alt="Professional kitchen cleaning service by Smith & Jengs in Bicester" loading="lazy" className="w-full h-full object-cover" data-id="element-290" />
            </motion.div>

            {/* Feature 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8,
            delay: 0.1
          }} className="flex flex-col gap-4" data-id="element-291">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center" data-id="element-292">
                <ShieldCheck className="w-7 h-7 text-primary" data-id="element-293" />
              </div>
              <h3 className="text-3xl md:text-4xl font-heading font-bold text-primary uppercase leading-tight tracking-wide" data-id="element-294">
                Safe, Insured,
                <br data-id="element-295" />
                and Vetted
              </h3>
              <p className="text-primary/70 text-lg leading-relaxed max-w-md" data-id="element-296">
                Our professionals are rigorously vetted, background checked, and
                fully insured for your peace of mind.
              </p>
            </motion.div>

            {/* Feature 4 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="flex flex-col gap-4" data-id="element-297">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center" data-id="element-298">
                <CreditCard className="w-7 h-7 text-primary" data-id="element-299" />
              </div>
              <h3 className="text-3xl md:text-4xl font-heading font-bold text-primary uppercase leading-tight tracking-wide" data-id="element-300">
                Secure, cash-free
                <br data-id="element-301" />
                payment
              </h3>
              <p className="text-primary/70 text-lg leading-relaxed max-w-md" data-id="element-302">
                Pay securely online. No need to worry about having cash on hand.
                We accept all major credit cards.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}